import React, { useState } from "react"
import {
  Drawer,
  Box,
  Typography,
  Stack,
  Button,
  IconButton,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Tooltip,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import AddIcon from "@mui/icons-material/Add"
import SaveIcon from "@mui/icons-material/Save"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import FolderOpenIcon from "@mui/icons-material/FolderOpen"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import DescriptionIcon from "@mui/icons-material/Description"
import PersonIcon from "@mui/icons-material/Person"
import PhoneIcon from "@mui/icons-material/Phone"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import EmailIcon from "@mui/icons-material/Email"
import EditIcon from "@mui/icons-material/Edit"
import { useTranslation } from "react-i18next"

const BRAND = { red: "#e50019", redDark: "#c40016", dark: "#212121", redLight: "#fde8ea", redText: "#9a0011" }

const STATUS_COLORS = {
  draft: { bg: "#fef3c7", color: "#92400e", label: "Draft" },
  saved: { bg: BRAND.redLight, color: BRAND.redText, label: "Saved" },
  sent: { bg: "#d1fae5", color: "#065f46", label: "Sent" },
  archived: { bg: "#f1f5f9", color: "#475569", label: "Archived" },
}

const timeAgo = (dateStr) => {
  if (!dateStr) return ""
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now - date
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return "just now"
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay === 1) return "yesterday"
  if (diffDay < 7) return `${diffDay}d ago`
  return date.toLocaleDateString("el-GR", { day: "numeric", month: "short" })
}

const QuoteCard = ({ quote, isActive, onLoad, onClone, onDelete, onEdit, t }) => {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const statusInfo = STATUS_COLORS[quote.status] || STATUS_COLORS.draft
  const itemCount = quote.item_count || 0
  const totalPrice = parseFloat(quote.total_price) || 0

  return (
    <Box
      sx={{
        p: 1.5,
        borderLeft: isActive ? `3px solid ${BRAND.red}` : "3px solid transparent",
        bgcolor: isActive ? BRAND.redLight : "#fff",
        "&:hover": { bgcolor: isActive ? BRAND.redLight : "#fafafa" },
        transition: "all 0.15s",
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "0.82rem",
              color: BRAND.dark,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {quote.title || "Untitled"}
          </Typography>
          {quote.customer_name && (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
              <PersonIcon sx={{ fontSize: 12, color: "#858585" }} />
              <Typography sx={{ fontSize: "0.68rem", color: "#858585" }}>
                {quote.customer_name}
              </Typography>
            </Stack>
          )}
          {quote.customer_phone && (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.15 }}>
              <PhoneIcon sx={{ fontSize: 11, color: "#ababab" }} />
              <Typography sx={{ fontSize: "0.62rem", color: "#ababab" }}>
                {quote.customer_phone}
              </Typography>
            </Stack>
          )}
        </Box>
        <Chip
          label={statusInfo.label}
          size="small"
          sx={{
            fontSize: "0.6rem",
            height: 18,
            bgcolor: statusInfo.bg,
            color: statusInfo.color,
            fontWeight: 600,
            ml: 0.5,
          }}
        />
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.75 }}>
        <Typography sx={{ fontSize: "0.68rem", color: "#858585" }}>
          {itemCount} {t("items")}
        </Typography>
        <Typography sx={{ fontSize: "0.68rem", color: "#858585" }}>&middot;</Typography>
        <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, color: BRAND.red }}>
          {"\u20AC"}{totalPrice.toFixed(2)}
        </Typography>
        <Typography sx={{ fontSize: "0.68rem", color: "#858585" }}>&middot;</Typography>
        <Typography sx={{ fontSize: "0.68rem", color: "#858585" }}>
          {timeAgo(quote.updated_at)}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
        {!isActive && (
          <Button
            size="small"
            startIcon={<FolderOpenIcon sx={{ fontSize: 14 }} />}
            onClick={() => onLoad(quote)}
            sx={{
              textTransform: "none",
              fontSize: "0.68rem",
              fontWeight: 600,
              color: BRAND.red,
              px: 1,
              py: 0.25,
              minWidth: 0,
              "&:hover": { bgcolor: BRAND.redLight },
            }}
          >
            {t("Load")}
          </Button>
        )}
        {isActive && (
          <>
            <Chip
              label={t("Active")}
              size="small"
              sx={{ fontSize: "0.6rem", height: 22, bgcolor: BRAND.red, color: "#fff", fontWeight: 600 }}
            />
            <IconButton
              size="small"
              onClick={() => onEdit(quote)}
              sx={{ color: "#858585", "&:hover": { color: BRAND.dark, bgcolor: "#f4f4f4" } }}
            >
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </>
        )}
        <Button
          size="small"
          startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
          onClick={() => onClone(quote)}
          sx={{
            textTransform: "none",
            fontSize: "0.68rem",
            fontWeight: 600,
            color: "#858585",
            px: 1,
            py: 0.25,
            minWidth: 0,
            "&:hover": { bgcolor: "#f4f4f4" },
          }}
        >
          {t("Clone")}
        </Button>
        {!confirmDelete ? (
          <IconButton
            size="small"
            onClick={() => setConfirmDelete(true)}
            sx={{ color: "#ababab", "&:hover": { color: BRAND.red, bgcolor: BRAND.redLight } }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ) : (
          <Button
            size="small"
            onClick={() => { onDelete(quote.id); setConfirmDelete(false) }}
            sx={{
              textTransform: "none",
              fontSize: "0.68rem",
              fontWeight: 600,
              px: 1,
              py: 0.25,
              minWidth: 0,
              color: BRAND.red,
              "&:hover": { bgcolor: BRAND.redLight },
            }}
          >
            {t("Confirm")}
          </Button>
        )}
      </Stack>
    </Box>
  )
}

const QuoteHistoryDrawer = ({
  open,
  onClose,
  quotes,
  activeQuoteId,
  activeQuote,
  loading,
  saving,
  onLoadQuote,
  onCloneQuote,
  onDeleteQuote,
  onNewQuote,
  onSaveQuote,
  saveDialogOpen,
  onOpenSaveDialog,
  onCloseSaveDialog,
}) => {
  const { t } = useTranslation()
  const [saveTitle, setSaveTitle] = useState("")
  const [saveCustomer, setSaveCustomer] = useState("")
  const [savePhone, setSavePhone] = useState("")
  const [saveEmail, setSaveEmail] = useState("")
  const [saveAddress, setSaveAddress] = useState("")

  // Pre-populate dialog when editing an existing quote
  const openEditDialog = (quote) => {
    setSaveTitle(quote?.title || "")
    setSaveCustomer(quote?.customer_name || "")
    setSavePhone(quote?.customer_phone || "")
    setSaveEmail(quote?.customer_email || "")
    setSaveAddress(quote?.customer_address || "")
    onOpenSaveDialog()
  }

  const handleSave = () => {
    onSaveQuote(saveTitle, saveCustomer, savePhone, saveEmail, saveAddress)
    setSaveTitle("")
    setSaveCustomer("")
    setSavePhone("")
    setSaveEmail("")
    setSaveAddress("")
    onCloseSaveDialog()
  }

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: 300, sm: 340 } } }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, bgcolor: BRAND.dark, borderBottom: `2px solid ${BRAND.red}` }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              <DescriptionIcon sx={{ fontSize: 20, color: BRAND.red }} />
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#fff" }}>
                {t("Quotes")}
              </Typography>
              <Chip
                label={quotes.length}
                size="small"
                sx={{ fontSize: "0.65rem", height: 20, bgcolor: "rgba(229,0,25,0.2)", color: BRAND.red, fontWeight: 700 }}
              />
            </Stack>
            <IconButton size="small" onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)" }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </Box>

        {/* New Quote button */}
        <Box sx={{ px: 2, py: 1.25 }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onNewQuote}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.82rem",
              borderColor: "#ebebeb",
              color: BRAND.red,
              borderRadius: 1.5,
              "&:hover": { borderColor: BRAND.red, bgcolor: BRAND.redLight },
            }}
          >
            {t("New Quote")}
          </Button>
        </Box>

        <Divider />

        {/* Quotes list */}
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} sx={{ color: BRAND.red }} />
            </Box>
          ) : quotes.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <DescriptionIcon sx={{ fontSize: 40, color: "#ebebeb", mb: 1 }} />
              <Typography sx={{ fontSize: "0.82rem", color: "#858585" }}>
                {t("No saved quotes yet")}
              </Typography>
              <Typography sx={{ fontSize: "0.7rem", color: "#ababab", mt: 0.5 }}>
                {t("Save your first composition")}
              </Typography>
            </Box>
          ) : (
            <Stack divider={<Divider />}>
              {quotes.map(quote => (
                <QuoteCard
                  key={quote.id}
                  quote={quote}
                  isActive={quote.id === activeQuoteId}
                  onLoad={onLoadQuote}
                  onClone={onCloneQuote}
                  onDelete={onDeleteQuote}
                  onEdit={openEditDialog}
                  t={t}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Drawer>

      {/* Save dialog */}
      <Dialog
        open={saveDialogOpen}
        onClose={onCloseSaveDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", pb: 0.5, color: BRAND.dark }}>
          {t("Save Quote")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <TextField
              label={t("Quote Title")}
              value={saveTitle}
              onChange={(e) => setSaveTitle(e.target.value)}
              placeholder={t("e.g. Kitchen Papadopoulos")}
              size="small"
              fullWidth
              autoFocus
            />
            <Divider sx={{ my: 0.5 }}>
              <Chip label={t("Customer")} size="small" sx={{ fontSize: "0.68rem", fontWeight: 600 }} />
            </Divider>
            <TextField
              label={t("Name")}
              value={saveCustomer}
              onChange={(e) => setSaveCustomer(e.target.value)}
              size="small"
              fullWidth
              InputProps={{ startAdornment: <PersonIcon sx={{ fontSize: 16, color: "#858585", mr: 0.75 }} /> }}
            />
            <Stack direction="row" spacing={1}>
              <TextField
                label={t("Phone")}
                value={savePhone}
                onChange={(e) => setSavePhone(e.target.value)}
                size="small"
                fullWidth
                InputProps={{ startAdornment: <PhoneIcon sx={{ fontSize: 16, color: "#858585", mr: 0.75 }} /> }}
              />
              <TextField
                label={t("Email")}
                value={saveEmail}
                onChange={(e) => setSaveEmail(e.target.value)}
                size="small"
                fullWidth
                InputProps={{ startAdornment: <EmailIcon sx={{ fontSize: 16, color: "#858585", mr: 0.75 }} /> }}
              />
            </Stack>
            <TextField
              label={t("Address")}
              value={saveAddress}
              onChange={(e) => setSaveAddress(e.target.value)}
              size="small"
              fullWidth
              InputProps={{ startAdornment: <LocationOnIcon sx={{ fontSize: 16, color: "#858585", mr: 0.75 }} /> }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onCloseSaveDialog} sx={{ textTransform: "none", color: "#858585" }}>
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              bgcolor: BRAND.red,
              "&:hover": { bgcolor: BRAND.redDark },
            }}
          >
            {t("Save")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default QuoteHistoryDrawer
